/**
 * <bundle-visual-sync>
 *
 * Synchronizes the custom bundle component cards with the product form's
 * variant id input. Two-way sync:
 *  - On custom swatch click → finds the matching parent bundle variant
 *    and updates `<input name="id">` of the product form (so ATC posts
 *    the correct variant).
 *  - Also forwards the click to the (hidden) native variant picker so
 *    the rest of the theme (price, gallery, sticky ATC) reacts.
 *
 * Reads variant mapping from a child <script type="application/json"
 * data-bv-bundle-variants> placed by the Liquid block.
 */

class BundleVisualSync extends HTMLElement {
  connectedCallback() {
    this.debug = this.hasAttribute('data-debug') || location.search.includes('debug_bundle');
    this.productId = this.dataset.productId || '';

    this.#loadVariantMap();
    this.#initSelectedOptions();
    this.#bindCustomButtons();
    this.#observeNativeChanges();
    this.#hookProductForm();

    requestAnimationFrame(() => this.#syncFromNative());
    setTimeout(() => this.#syncFromNative(), 500);
  }

  #patchFetch() {
    if (window.__bvFetchPatched) return;
    window.__bvFetchPatched = true;

    const originalFetch = window.fetch.bind(window);
    const self = this;

    window.fetch = async function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (!url || !url.includes('/cart/add')) {
          return originalFetch(input, init);
        }

        const matched = self.#computeMatchedVariant();
        if (!matched) return originalFetch(input, init);

        const opts = init || (input instanceof Request ? { method: input.method, headers: input.headers, body: input.body } : {});

        if (opts.body instanceof FormData) {
          if (String(opts.body.get('id')) !== String(matched.id)) {
            console.warn('[bundle-visual-sync] FETCH intercept: rewriting FormData id', opts.body.get('id'), '→', matched.id);
            opts.body.set('id', matched.id);
          }
        } else if (typeof opts.body === 'string') {
          try {
            const parsed = JSON.parse(opts.body);
            if (parsed && Array.isArray(parsed.items)) {
              let changed = false;
              parsed.items.forEach((it) => {
                if (it && it.id && parsed.items.length === 1) {
                  if (String(it.id) !== String(matched.id)) {
                    console.warn('[bundle-visual-sync] FETCH intercept: rewriting JSON item id', it.id, '→', matched.id);
                    it.id = Number(matched.id);
                    changed = true;
                  }
                }
              });
              if (changed) opts.body = JSON.stringify(parsed);
            } else if (parsed && parsed.id) {
              if (String(parsed.id) !== String(matched.id)) {
                console.warn('[bundle-visual-sync] FETCH intercept: rewriting JSON id', parsed.id, '→', matched.id);
                parsed.id = Number(matched.id);
                opts.body = JSON.stringify(parsed);
              }
            }
          } catch (_) {}
        } else if (opts.body instanceof URLSearchParams) {
          if (String(opts.body.get('id')) !== String(matched.id)) {
            console.warn('[bundle-visual-sync] FETCH intercept: rewriting URLSearchParams id', opts.body.get('id'), '→', matched.id);
            opts.body.set('id', matched.id);
          }
        }

        if (input instanceof Request) {
          return originalFetch(new Request(input.url, opts));
        }
        return originalFetch(input, opts);
      } catch (e) {
        console.error('[bundle-visual-sync] fetch patch error', e);
        return originalFetch(input, init);
      }
    };
  }

  #computeMatchedVariant() {
    if (!this.bundleVariants || this.bundleVariants.length === 0) return null;
    return this.bundleVariants.find((v) =>
      Object.entries(this.selectedOptions).every(
        ([k, val]) => v.options && v.options[k] === val
      )
    );
  }

  #hookProductForm() {
    this.#patchFetch();
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach((form) => {
      form.addEventListener(
        'submit',
        (e) => {
          this.#updateProductFormVariant();

          const fd = new FormData(form);
          const idValue = fd.get('id');
          console.log('[bundle-visual-sync] SUBMIT formData id =', idValue);

          if (this.bundleVariants && this.bundleVariants.length > 0) {
            const matched = this.bundleVariants.find((v) =>
              Object.entries(this.selectedOptions).every(
                ([k, val]) => v.options && v.options[k] === val
              )
            );
            if (matched && String(idValue) !== String(matched.id)) {
              console.warn(
                '[bundle-visual-sync] formData id mismatch! expected',
                matched.id,
                'got',
                idValue,
                '— forcing fix'
              );
              const allIdInputs = form.querySelectorAll('input[name="id"]');
              allIdInputs.forEach((input) => {
                input.value = matched.id;
              });
            }
          }
        },
        true
      );

      const buyButtons = form.querySelectorAll(
        'button[type="submit"], [name="add"], add-to-cart-component button, .shopify-payment-button button, [data-shopify="payment-button"] button'
      );
      buyButtons.forEach((btn) => {
        btn.addEventListener(
          'pointerdown',
          () => {
            this.#updateProductFormVariant();
          },
          true
        );
        btn.addEventListener(
          'click',
          () => {
            this.#updateProductFormVariant();
          },
          true
        );
      });
    });

    document.addEventListener(
      'pointerdown',
      (e) => {
        const t = e.target;
        if (!(t instanceof HTMLElement)) return;
        if (
          t.closest(
            '.shopify-payment-button, [data-shopify="payment-button"], add-to-cart-component, [name="add"], button[type="submit"]'
          )
        ) {
          this.#updateProductFormVariant();
        }
      },
      true
    );
  }

  #loadVariantMap() {
    this.bundleVariants = [];
    const script = this.querySelector('script[data-bv-bundle-variants]');
    if (!script) return;
    try {
      this.bundleVariants = JSON.parse(script.textContent || '[]');
      if (this.debug) console.log('[bundle-visual-sync] variants loaded:', this.bundleVariants);
    } catch (e) {
      console.warn('[bundle-visual-sync] could not parse variants JSON', e);
    }
  }

  #initSelectedOptions() {
    this.selectedOptions = {};
    this.querySelectorAll('.cds-bv__swatch.is-active').forEach((btn) => {
      const name = btn.getAttribute('data-bv-option-name');
      const value = btn.getAttribute('data-bv-option-value');
      if (name && value) this.selectedOptions[name] = value;
    });

    this.querySelectorAll('.cds-bv__card-picker').forEach((picker) => {
      const hasActive = picker.querySelector('.cds-bv__swatch.is-active');
      if (hasActive) return;

      const candidate =
        picker.querySelector('.cds-bv__swatch:not(.cds-bv__swatch--unavailable)') ||
        picker.querySelector('.cds-bv__swatch');
      if (!candidate) return;

      candidate.classList.add('is-active');
      candidate.setAttribute('aria-checked', 'true');
      const name = candidate.getAttribute('data-bv-option-name');
      const value = candidate.getAttribute('data-bv-option-value');
      if (name && value) this.selectedOptions[name] = value;
    });

    requestAnimationFrame(() => this.#updateProductFormVariant());
  }

  #bindCustomButtons() {
    this.querySelectorAll('.cds-bv__swatch').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (btn.getAttribute('aria-disabled') === 'true') return;

        const optionName = btn.getAttribute('data-bv-option-name');
        const value = btn.getAttribute('data-bv-option-value');
        const variantImage = btn.getAttribute('data-bv-variant-image');

        if (optionName && value) this.selectedOptions[optionName] = value;

        this.#updateProductFormVariant();
        this.#triggerNativeRadio(optionName, value, btn);
        if (variantImage) this.#updateCardImage(btn, variantImage);

        this.#optimisticUpdate(btn);
        this.#updatePickerValueLabels();
      });
    });
  }

  #findProductForm() {
    const candidates = [
      `form[action*="/cart/add"][data-product-id="${this.productId}"]`,
      `form[action*="/cart/add"][id*="${this.productId}"]`,
      'product-form form[action*="/cart/add"]',
      'form[action$="/cart/add"]',
      'form[action*="/cart/add"]',
    ];
    for (const sel of candidates) {
      const form = document.querySelector(sel);
      if (form) return form;
    }
    return null;
  }

  #updateProductFormVariant() {
    if (!this.bundleVariants || this.bundleVariants.length === 0) return;

    const matched = this.bundleVariants.find((v) => {
      return Object.entries(this.selectedOptions).every(
        ([k, val]) => v.options && v.options[k] === val
      );
    });

    if (this.debug) {
      console.log(
        '[bundle-visual-sync] selectedOptions:',
        this.selectedOptions,
        '→ matched variant:',
        matched
      );
    }

    if (!matched) return;

    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (!forms.length) {
      if (this.debug) console.warn('[bundle-visual-sync] no /cart/add form found');
      return;
    }

    forms.forEach((form) => {
      let idInput = form.querySelector('input[name="id"]');
      if (!idInput) {
        idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.setAttribute('data-bv-injected', '1');
        form.appendChild(idInput);
      }

      const allIdInputs = form.querySelectorAll('input[name="id"]');
      allIdInputs.forEach((input) => {
        if (String(input.value) !== String(matched.id)) {
          input.value = matched.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });

      const pf =
        form.closest('product-form') ||
        form.closest('product-form-component') ||
        document.querySelector('product-form-component');
      if (pf) {
        if (pf.dataset) pf.dataset.variantId = matched.id;
        if (pf.refs && pf.refs.variantId) {
          pf.refs.variantId.value = matched.id;
        }
      }
    });

    document.querySelectorAll('input[name="id"]').forEach((input) => {
      const inForm = input.closest('form[action*="/cart/add"]');
      if (inForm) return;
      if (String(input.value) !== String(matched.id)) {
        input.value = matched.id;
      }
    });

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== String(matched.id)) {
        url.searchParams.set('variant', matched.id);
        window.history.replaceState({}, '', url.toString());
      }
    } catch (_) {}

    if (this.debug) {
      const allInputs = document.querySelectorAll('input[name="id"]');
      console.log(
        '[bundle-visual-sync] all input[name=id] values now:',
        Array.from(allInputs).map((i) => ({
          form: i.closest('form')?.id || 'no-form',
          value: i.value,
          injected: i.hasAttribute('data-bv-injected'),
        }))
      );
    }
  }

  #findRadios() {
    return Array.from(document.querySelectorAll('input[type="radio"]')).filter((r) => {
      const fs = r.closest('fieldset.variant-option, fieldset[class*="variant"]');
      const inPicker = r.closest('swatches-variant-picker-component, variant-picker');
      return Boolean(fs || inPicker);
    });
  }

  #findMatchingRadio(optionName, value) {
    if (!optionName || value == null) return null;
    const radios = this.#findRadios();

    let target = radios.find(
      (r) => r.name && r.name.startsWith(optionName + '-') && r.value === value
    );
    if (target) return target;

    target = radios.find((r) => r.name && r.name.includes(optionName) && r.value === value);
    if (target) return target;

    target = radios.find((r) => r.value === value);
    return target || null;
  }

  #triggerNativeRadio(optionName, value, srcBtn) {
    const target = this.#findMatchingRadio(optionName, value);

    if (!target) {
      if (this.debug) {
        console.warn('[bundle-visual-sync] no native radio for', { optionName, value });
      }
      return;
    }

    const label = target.closest('label');
    target.checked = true;

    target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(new Event('change', { bubbles: true }));
    if (label) label.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

    setTimeout(() => this.#syncFromNative(), 50);
  }

  #optimisticUpdate(srcBtn) {
    if (!srcBtn) return;
    const optionPos = srcBtn.getAttribute('data-bv-option-position');
    this.querySelectorAll(
      `.cds-bv__swatch[data-bv-option-position="${optionPos}"]`
    ).forEach((btn) => {
      const isSrc = btn === srcBtn;
      btn.classList.toggle('is-active', isSrc);
      btn.setAttribute('aria-checked', isSrc ? 'true' : 'false');
    });
  }

  #updateCardImage(srcBtn, imageUrl) {
    if (!imageUrl || !srcBtn) return;
    const card = srcBtn.closest('.cds-bv__card');
    if (!card) return;

    const img = card.querySelector('.cds-bv__card-img');
    if (!img || img.classList.contains('cds-bv__card-img--placeholder')) return;

    img.style.opacity = '0.5';
    img.style.transition = 'opacity 0.2s ease';

    const swap = () => {
      img.setAttribute('src', imageUrl);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      requestAnimationFrame(() => {
        img.style.opacity = '1';
      });
    };

    if (img.complete) {
      swap();
    } else {
      img.addEventListener('load', swap, { once: true });
      setTimeout(swap, 200);
    }
  }

  #observeNativeChanges() {
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== 'radio') return;
      const inPicker = target.closest(
        'swatches-variant-picker-component, variant-picker'
      );
      if (!inPicker) return;
      this.#syncFromNative();
    });
  }

  #syncFromNative() {
    const radios = this.#findRadios();
    const checkedByOption = new Map();

    radios.forEach((r) => {
      if (!r.checked) return;
      const namePrefix = (r.name || '').split('-').slice(0, -2).join('-');
      const optName = namePrefix || r.name;
      checkedByOption.set(optName, r.value);
      this.selectedOptions[optName] = r.value;
    });

    this.querySelectorAll('.cds-bv__swatch').forEach((btn) => {
      const optName = btn.getAttribute('data-bv-option-name');
      const value = btn.getAttribute('data-bv-option-value');
      if (!optName) return;

      let nativeValue = null;
      if (checkedByOption.has(optName)) {
        nativeValue = checkedByOption.get(optName);
      } else {
        for (const [k, v] of checkedByOption) {
          if (k.includes(optName) || optName.includes(k)) {
            nativeValue = v;
            break;
          }
        }
      }

      if (nativeValue == null) return;

      const active = nativeValue === value;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    this.#updatePickerValueLabels();
    this.#updateProductFormVariant();
  }

  #updatePickerValueLabels() {
    this.querySelectorAll('.cds-bv__card-picker').forEach((picker) => {
      const valueSpan = picker.querySelector('[data-bv-picker-value]');
      if (!valueSpan) return;

      const activeBtn = picker.querySelector('.cds-bv__swatch.is-active');
      if (activeBtn) {
        const value = activeBtn.getAttribute('data-bv-option-value');
        valueSpan.textContent = value ? ' : ' + value : '';
      } else {
        valueSpan.textContent = '';
      }
    });
  }
}

if (!customElements.get('bundle-visual-sync')) {
  customElements.define('bundle-visual-sync', BundleVisualSync);
}
